import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase';
import { useAdmin } from './AdminContext';
import { REALMS } from '../../../../shared/src/realms';

interface QuestionRow {
  id: string; realm: string; sub_topic: string | null; difficulty: string;
  question_text: string; choices: string[]; correct_index: number;
  explanation: string | null;
  // Set by audit-questions. Null on rows that are here simply because they are
  // new rather than because something is wrong with them.
  audit_flags: string[] | null;
  audit_note: string | null;
  est_p: number | null;
}

export function ReviewQueue() {
  const { refreshStats, toast } = useAdmin();
  const [queue, setQueue] = useState<QuestionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    // Audit findings come first: a question is in this queue either because it
    // is new, or because the auditor found something wrong with it. Reviewing
    // the second kind without seeing the finding means re-doing the audit by
    // hand, so the flags and the note travel with the row.
    const { data } = await supabase.rpc('admin_questions', {
      p_reviewed: false,
      p_order: 'review_queue',
      p_limit: 100,
    });
    setQueue(data || []);
    setCurrentIdx(0);
    setLoading(false);
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  /**
   * Row-level security does not raise when it filters a write: the statement
   * succeeds having changed nothing. That is how approving a question could
   * appear to work and then bring it straight back on refresh. Asking for the
   * changed rows back turns the silent no-op into something we can catch.
   */
  const writeOrThrow = async (
    run: () => PromiseLike<{ data: unknown[] | null; error: { message: string } | null }>,
  ) => {
    const { data, error } = await run();
    if (error) throw new Error(error.message);
    if (!data || data.length === 0) {
      throw new Error('Write was blocked: no rows changed. Check the admin policy on questions.');
    }
  };

  const approve = useCallback(async (id: string) => {
    const snapshot = queue;
    setQueue(prev => prev.filter(q => q.id !== id));
    try {
      await writeOrThrow(() =>
        supabase.from('questions').update({ reviewed: true }).eq('id', id).select('id'));
      refreshStats();
    } catch (e) {
      // Put it back rather than leaving the operator believing it is done.
      setQueue(snapshot);
      toast(e instanceof Error ? e.message : 'Approve failed', 'error');
    }
  }, [queue, refreshStats, toast]);

  const reject = useCallback(async (id: string) => {
    const snapshot = queue;
    setQueue(prev => prev.filter(q => q.id !== id));
    try {
      await writeOrThrow(() =>
        supabase.from('questions').delete().eq('id', id).select('id'));
      toast('Question rejected', 'info');
      refreshStats();
    } catch (e) {
      setQueue(snapshot);
      toast(e instanceof Error ? e.message : 'Reject failed', 'error');
    }
  }, [queue, refreshStats, toast]);

  const approveAll = async () => {
    if (!window.confirm(`Approve all ${queue.length} questions?`)) return;
    const ids = queue.map(q => q.id);
    const snapshot = queue;
    setQueue([]);
    try {
      // One statement instead of a loop: the whole batch either lands or it
      // does not, and the returned rows say which.
      const { data, error } = await supabase
        .from('questions').update({ reviewed: true }).in('id', ids).select('id');
      if (error) throw new Error(error.message);
      const n = data?.length ?? 0;
      if (n === 0) throw new Error('Write was blocked: no rows changed. Check the admin policy on questions.');
      toast(`${n} questions approved${n < ids.length ? ` (${ids.length - n} unchanged)` : ''}`, 'success');
      refreshStats();
    } catch (e) {
      setQueue(snapshot);
      toast(e instanceof Error ? e.message : 'Approve failed', 'error');
    }
  };

  const current = queue[currentIdx] || null;

  // Keyboard shortcuts
  useEffect(() => {
    if (!current) return;
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'a' || e.key === 'A') { approve(current.id); }
      if (e.key === 'r' || e.key === 'R') { reject(current.id); }
      if (e.key === 'ArrowRight') { setCurrentIdx(i => Math.min(i + 1, queue.length - 1)); }
      if (e.key === 'ArrowLeft') { setCurrentIdx(i => Math.max(0, i - 1)); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [current, approve, reject, queue.length]);

  // Keep idx in bounds
  useEffect(() => {
    if (currentIdx >= queue.length && queue.length > 0) setCurrentIdx(queue.length - 1);
  }, [queue.length, currentIdx]);

  if (loading) return <div style={{ textAlign: 'center', padding: '60px', color: '#808080' }}>Loading review queue...</div>;

  if (queue.length === 0) {
    return (
      <div className="admin-card" style={{ textAlign: 'center', padding: '80px 40px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✅</div>
        <h3 style={{ fontFamily: 'var(--font-heading)', color: '#14b8a6', margin: '0 0 8px 0', fontSize: '1.2rem' }}>ALL CLEAR</h3>
        <p style={{ color: '#808080', fontSize: '0.85rem' }}>No unreviewed questions remaining.</p>
        <button className="admin-btn admin-btn-secondary" style={{ marginTop: '16px' }} onClick={fetchQueue}>↻ REFRESH</button>
      </div>
    );
  }

  const pct = Math.round(((queue.length - (queue.length - currentIdx)) / queue.length) * 100);
  const realmColor = REALMS[current?.realm as keyof typeof REALMS]?.color || '#ccccd4';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 className="admin-section-title" style={{ margin: 0 }}>REVIEW QUEUE</h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', fontFamily: 'var(--font-mono)', color: '#808080' }}>{queue.length} remaining</span>
          <button className="admin-btn admin-btn-success admin-btn-sm" onClick={approveAll}>APPROVE ALL ✓</button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="admin-progress" style={{ marginBottom: '8px' }}>
        <div className="admin-progress-bar" style={{ width: `${pct}%` }} />
      </div>
      <div style={{ textAlign: 'center', fontSize: '0.85rem', fontFamily: 'var(--font-mono)', color: '#444', marginBottom: '24px' }}>
        {currentIdx + 1} / {queue.length}
      </div>

      {current && (
        <div className="admin-card" style={{ maxWidth: '700px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ color: realmColor, fontFamily: 'var(--font-heading)', fontSize: '0.8rem', fontWeight: 'bold' }}>{current.realm}</span>
              {current.sub_topic && <span style={{ color: '#444', fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}>/ {current.sub_topic}</span>}
              <span className={`admin-badge admin-badge-${current.difficulty}`}>{current.difficulty.toUpperCase()}</span>
            </div>
          </div>

          {/* Auditor's finding, above the question: it is the reason this row
              is here, so it has to be read before the question, not after. */}
          {Array.isArray(current.audit_flags) && current.audit_flags.length > 0 && (
            <div style={{
              background: '#1a0f14', border: '1px solid #f43f5e44', borderRadius: 8,
              padding: '12px 14px', marginBottom: 20,
            }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {current.audit_flags.map((f: string) => (
                  <span key={f} style={{
                    fontFamily: 'var(--font-mono)', fontSize: '0.8rem', letterSpacing: '0.08em',
                    padding: '3px 8px', borderRadius: 4,
                    // The two that pulled the question out of play read louder
                    // than the two that are judgement calls.
                    background: f === 'answer_wrong' || f === 'explanation_mismatch' ? '#f43f5e' : '#3a2a35',
                    color: f === 'answer_wrong' || f === 'explanation_mismatch' ? '#000' : '#c9a5b5',
                    fontWeight: 700,
                  }}>{f.replace(/_/g, ' ').toUpperCase()}</span>
                ))}
                {current.est_p != null && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: '#bebec6', padding: '3px 4px' }}>
                    p={Number(current.est_p).toFixed(2)}
                  </span>
                )}
              </div>
              {current.audit_note && (
                <div style={{ fontSize: '0.8rem', color: '#e0c8d0', lineHeight: 1.55 }}>
                  {current.audit_note}
                </div>
              )}
              <div style={{ fontSize: '0.82rem', color: '#7a6570', marginTop: 8, fontStyle: 'italic' }}>
                The auditor hedges. If the note argues itself down, trust the question.
              </div>
            </div>
          )}

          {/* Question */}
          <div style={{ fontSize: '1.15rem', color: '#eee', lineHeight: 1.6, marginBottom: '24px', fontWeight: 500 }}>
            {current.question_text}
          </div>

          {/* Choices */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
            {current.choices.map((c, i) => (
              <div key={i} style={{
                padding: '12px 16px', borderRadius: '8px', fontSize: '0.9rem',
                background: i === current.correct_index ? '#14b8a618' : '#0a0a0a',
                color: i === current.correct_index ? '#14b8a6' : '#c4c4cc',
                border: `1px solid ${i === current.correct_index ? '#14b8a644' : '#243450'}`,
                display: 'flex', gap: '10px', alignItems: 'center',
              }}>
                <span style={{ width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.85rem', fontWeight: 'bold', background: i === current.correct_index ? '#14b8a622' : '#16223A', color: i === current.correct_index ? '#14b8a6' : '#808080' }}>
                  {['A', 'B', 'C', 'D'][i]}
                </span>
                {c}
                {i === current.correct_index && <span style={{ marginLeft: 'auto', fontSize: '0.85rem' }}>✓ correct</span>}
              </div>
            ))}
          </div>

          {/* Explanation */}
          {current.explanation && (
            <div style={{ padding: '12px 16px', background: '#0a0a1a', border: '1px solid #1a1a3a', borderRadius: '8px', fontSize: '0.85rem', color: '#818cf8', marginBottom: '20px', fontStyle: 'italic' }}>
              💡 {current.explanation}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button className="admin-btn admin-btn-success" style={{ padding: '12px 28px', fontSize: '0.9rem' }} onClick={() => approve(current.id)}>
              ✓ APPROVE <span style={{ fontSize: '0.8rem', opacity: 0.6, marginLeft: '4px' }}>(A)</span>
            </button>
            <button className="admin-btn admin-btn-danger" style={{ padding: '12px 28px', fontSize: '0.9rem' }} onClick={() => reject(current.id)}>
              ✕ REJECT <span style={{ fontSize: '0.8rem', opacity: 0.6, marginLeft: '4px' }}>(R)</span>
            </button>
          </div>

          {/* Keyboard hint */}
          <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '0.8rem', fontFamily: 'var(--font-mono)', color: '#3E4E70' }}>
            A = approve · R = reject · ← → = navigate
          </div>
        </div>
      )}
    </div>
  );
}
